import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Divider, Button, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useReactToPrint } from 'react-to-print';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useSelector } from "react-redux";
import moment from 'moment';
import { currencyFormatter, withoutSymbolFormatter } from 'utilities/currencyFormatter';
import axios from 'axios';
import { baseURL } from 'services/API';

// Define the theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#000000',
    },
    secondary: {
      main: '#FFD700',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

const TenantReceipt = () => {
  const receiptRef = useRef();
  const tenantPaymentData = useSelector((state) => state.tenantPayment);
  // Payment method is now an object with name property

  // Extract data from the stored payment information
  const periodPayment = tenantPaymentData?.periodPayment || tenantPaymentData;
  const individualPayment = tenantPaymentData?.individualPayment;

  // Check if this is an arrears record
  const isArrearsRecord = periodPayment?.is_arrears_record || false;
  const arrearsData = periodPayment?.arrears_data || {};

  // Function to print the receipt
  const handlePrint = useReactToPrint({
    content: () => receiptRef.current,
  });

  // Function to download the receipt as a PDF
  const handleDownload = async () => {
    const input = receiptRef.current;
    const canvas = await html2canvas(input);
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    pdf.addImage(imgData, 'PNG', 0, 0);
    pdf.save(isArrearsRecord ? "arrears-statement.pdf" : "tenant-receipt.pdf");
  };

  // Determine if this is an individual payment receipt or period receipt
  const isIndividualPayment = !!individualPayment;

  // Get the payment details
  const getPaymentDetails = () => {
    if (isArrearsRecord && arrearsData) {
      // Arrears Statement
      return {
        tenantName: arrearsData.tenant_name || 'N/A',
        phoneNumber: 'N/A',
        propertyName: `${arrearsData.unit_name || ''} ${arrearsData.property_name || ''}`,
        paymentDate: arrearsData.arrears_date || new Date(),
        amount: arrearsData.amount_in_arrears || 0,
        paymentMethod: 'N/A',
        monthPaidFor: `${new Date(arrearsData.period_start_date).toLocaleDateString('en-US', { month: 'long' })} ${new Date(arrearsData.period_start_date).getFullYear()}`,
        receiptDate: new Date()
      };
    } else if (isIndividualPayment && individualPayment) {
      // Individual Payment Receipt
      const amount = individualPayment.amount || individualPayment.amount_paid || 0;
      const paymentMethod = individualPayment.payment_method?.name || 'Unknown';
      const monthPaidFor = individualPayment.related_month ? 
        `${individualPayment.related_month.month} ${individualPayment.related_month.year}` : 
        'Unknown Month';
      
      return {
        tenantName: periodPayment.related_occupancy.related_tenant.first_name + ' ' + periodPayment.related_occupancy.related_tenant.last_name,
        phoneNumber: periodPayment.related_occupancy.related_tenant.phone_number,
        propertyName: periodPayment.related_occupancy.related_rental_unit.unit_name + ' ' + periodPayment.related_occupancy.related_rental_unit.unit_type,
        paymentDate: individualPayment.date_paid || individualPayment.date_created,
        amount: amount,
        paymentMethod: paymentMethod,
        monthPaidFor: monthPaidFor,
        receiptDate: new Date()
      };
    } else {
      // Fallback to period receipt (existing functionality)
      return {
        tenantName: periodPayment.related_occupancy.related_tenant.first_name + ' ' + periodPayment.related_occupancy.related_tenant.last_name,
        phoneNumber: periodPayment.related_occupancy.related_tenant.phone_number,
        propertyName: periodPayment.related_occupancy.related_rental_unit.unit_name + ' ' + periodPayment.related_occupancy.related_rental_unit.unit_type,
        paymentDate: periodPayment.related_active_period.date_started,
        amount: periodPayment.total_amout_paid,
        paymentMethod: 'Multiple',
        monthPaidFor: periodPayment.related_active_period.related_month.month + ' ' + periodPayment.related_active_period.related_month.year,
        receiptDate: new Date()
      };
    }
  };

  const paymentDetails = getPaymentDetails();

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', 
               margin:"20px 0 0 0", padding:"0 20 20 80", alignItems:"center" }}>
       
        <Box sx={{ display: 'flex',   width:"210mm"}}>
        
          <Box sx={{ flexGrow: 1, backgroundColor: '#f9f9f9', overflowY: 'auto'}}>
            {/* Receipt Container */}
            <Box ref={receiptRef} sx={{ 
              paddingBottom: 8, 
              backgroundColor: 'white', 
              minHeight: 'calc(100vh - 60px - 80px - 80px)',
              overflowY: 'auto',
              marginBottom: 8
            }}>
              {/* Header */}
              <Box sx={{ backgroundColor: 'black', padding: 2, marginTop: 2, marginLeft: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <img src="/assets/nav-image.png" alt="RentBeta Logo" style={{ height: 100 }} />
                <Box sx={{ textAlign: 'right', color: 'gold' }}>
                  <Typography variant="body2" sx={{ color: 'gold', fontWeight: 'bold' }}>
                    ICT Innovation Hub Nakawa, inside UICT
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'gold', fontWeight: 'bold' }}>
                    Plot 19-12 Port Bell Road, Kampala, Uganda
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'gold', fontWeight: 'bold' }}>
                    Tel: +256 703330943 / +256 774453453
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'gold', fontWeight: 'bold' }}>
                    Website: <a href="https://www.rentbeta.africa" style={{ color: 'gold', textDecoration: 'none' }}>www.rentbeta.africa</a>
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'gold', fontWeight: 'bold' }}>
                    Email: admin@rentbeta.africa
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ marginTop: 5, marginX:"5px" }}>
                <Typography variant="h5" sx={{ color: 'black', textAlign: 'center', fontWeight: 'bold' }}>
                  {isArrearsRecord ? 'Rent Arrears Statement' : (isIndividualPayment ? 'Individual Payment Receipt' : 'Rent Payment Receipt')}
                </Typography>
                <Divider sx={{ backgroundColor: 'black' }} />
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', marginTop: 2 }}>
                  <Box sx={{ flex: 1, padding: 1 }}>
                    <Typography variant="body1" sx={{ marginBottom: 1 }}>
                      <strong>Tenant Name:</strong> {paymentDetails.tenantName}
                    </Typography>
                    <Typography variant="body1" sx={{ marginBottom: 1 }}>
                      <strong>{isArrearsRecord ? 'Statement Date:' : 'Receipt Date:'}</strong> {moment(paymentDetails.receiptDate).format('L')}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, padding: 1 }}>
                    <Typography variant="body1" sx={{ marginBottom: 1 }}>
                      <strong>Telephone Number:</strong> {paymentDetails.phoneNumber}
                    </Typography>
                    <Typography variant="body1" sx={{ marginBottom: 1 }}>
                      <strong>Rental Property:</strong> {paymentDetails.propertyName}
                    </Typography>
                  </Box>
                </Box>
                
                <Divider sx={{ backgroundColor: 'black' }} />

                <Typography variant="h6" sx={{ color: 'black', margin: 2 }}>
                  {isArrearsRecord ? 'Arrears Details' : (isIndividualPayment ? 'Payment Details' : 'Payment Period')}
                </Typography>

                <Box sx={{ backgroundColor: 'black', color: 'white', padding: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 1, marginX:"5px"  }}>
                  <Typography variant="body1" sx={{ flex: 2, fontWeight: 'bold' }}>
                    Description
                  </Typography>
                  <Typography variant="body1" sx={{ flex: 1, textAlign: 'center', fontWeight: 'bold' }}>
                    Amount
                  </Typography>
                </Box>
              </Box>
              
              {/* Receipt Items */}
              <Box sx={{ backgroundColor: 'white', margin: 0, padding:2, }}>
                {isArrearsRecord ? (
                  // Arrears Statement Content
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ flex: 2 }}>
                        Rent for {moment(arrearsData.period_start_date).format('MMMM YYYY')} ({moment(arrearsData.period_start_date).format('L')} - {moment(arrearsData.period_end_date).format('L')})
                      </Typography>
                      <Typography variant="body1" sx={{ flex: 1, textAlign: 'center' }}>
                        {currencyFormatter(parseFloat(arrearsData.rent_amount))}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ flex: 2 }}>
                        Amount Paid
                      </Typography>
                      <Typography variant="body1" sx={{ flex: 1, textAlign: 'center' }}>
                        {currencyFormatter(parseFloat(arrearsData.paid_amount))}
                      </Typography>
                    </Box>
                    <Divider sx={{ marginY: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ flex: 2, fontWeight: 'bold', color: 'error.main' }}>
                        Amount in Arrears
                      </Typography>
                      <Typography variant="body1" sx={{ flex: 1, textAlign: 'center', fontWeight: 'bold', color: 'error.main' }}>
                        {currencyFormatter(parseFloat(arrearsData.amount_in_arrears))}
                      </Typography>
                    </Box>
                  </>
                ) : isIndividualPayment ? (
                  // Individual Payment Receipt
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ flex: 2 }}>
                        Rent payment for {paymentDetails.monthPaidFor}
                      </Typography>
                      <Typography variant="body1" sx={{ flex: 1, textAlign: 'center' }}>
                        {currencyFormatter(parseFloat(paymentDetails.amount) || 0)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 1 }}>
                      <Typography variant="body1" sx={{ flex: 2 }}>
                        Payment Date: {moment(paymentDetails.paymentDate).format('LL')}
                      </Typography>
                      <Typography variant="body1" sx={{ flex: 1, textAlign: 'center' }}>
                        {paymentDetails.paymentMethod}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  // Period Receipt (existing functionality)
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ flex: 2 }}>
                        Rent for {periodPayment.related_active_period.related_month.month} {periodPayment.related_active_period.related_month.year} starting {moment(periodPayment.related_active_period.date_started).format('L')} to {moment(periodPayment.related_active_period.date_ended).format('L')} 
                      </Typography>
                      <Typography variant="body1" sx={{ flex: 1, textAlign: 'center' }}>
                        {periodPayment.period_rent}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ flex: 2 }}>
                        Outstanding Balance
                      </Typography>
                      <Typography variant="body1" sx={{ flex: 1, textAlign: 'center' }}>
                        {periodPayment.outstanding_balance}
                      </Typography>
                    </Box>
                  </>
                )}
                <Divider/>
              </Box>

              {/* Additional Information for Arrears */}
              {isArrearsRecord && (
                <Box sx={{ backgroundColor: 'white', marginTop: "1px", padding: 2,  marginX:"5px"  }}>
                  <Typography variant="h6" sx={{ color: 'black', marginX: 2,  }}>Additional Information</Typography>
                  <Box sx={{ display: 'flex', padding: 1 }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ flex: 1 }}>Days in Arrears:</Typography>
                    <Typography variant="body1" sx={{ flex: 1, textAlign: 'center' }}>
                      {arrearsData.days_in_arrears} days
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', padding: 1 }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ flex: 1 }}>Period Status:</Typography>
                    <Typography variant="body1" sx={{ flex: 1, textAlign: 'center' }}>
                      {arrearsData.is_current_period ? 'Current Period' : 'Previous Period'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', padding: 1 }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ flex: 1 }}>Arrears Date:</Typography>
                    <Typography variant="body1" sx={{ flex: 1, textAlign: 'center' }}>
                      {moment(arrearsData.arrears_date).format('LL')}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Summary Section */}
              <Box sx={{ backgroundColor: 'white', marginTop: "1px", padding: 2,  marginX:"5px"  }}>
                <Typography variant="h6" sx={{ color: 'black', marginX: 2,  }}>Summary</Typography>
                <Box sx={{ display: 'flex', padding: 1 }}>
                  <Typography variant="body1" fontWeight="bold" sx={{ flex: 1 }}>
                    {isArrearsRecord ? 'AMOUNT IN ARREARS:' : (isIndividualPayment ? 'PAYMENT AMOUNT:' : 'TOTAL PAYMENTS MADE:')}
                  </Typography>
                  <Typography variant="body1" sx={{ flex: 1, textAlign: 'center' }}>
                    {currencyFormatter(parseFloat(paymentDetails.amount) || 0)}
                  </Typography>
                </Box>
                {!isIndividualPayment && !isArrearsRecord && (
                  <Box sx={{ display: 'flex', padding: 1 }}>
                    <Typography variant="body1" fontWeight="bold" sx={{ flex: 1 }}>REMAINING BALANCE:</Typography>
                    <Typography variant="body1" sx={{ flex: 1, textAlign: 'center' }}>
                      {currencyFormatter(parseFloat(periodPayment.balance_carried))}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Divider/>

              {/* Footer */}
              <Box sx={{ textAlign: 'center', marginTop: 4 }}>
                <Typography variant="body1" sx={{ color: 'black', fontWeight: 'bold' }}>
                  Powered by RentBeta.africa
                </Typography>
                <Typography variant="body3" sx={{ color: 'black'}}>
                  {isArrearsRecord 
                    ? 'This is an autogenerated arrears statement. Please contact management for payment arrangements.'
                    : 'This is an autogenerated receipt. Do not sign'
                  }
                </Typography>
              </Box>
            </Box>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'center', paddingY: 2, paddingTop:0 }}>
              <Button variant="contained" color="primary" onClick={handlePrint} sx={{ marginRight: 2 }}>
                {isArrearsRecord ? 'Print Statement' : 'Print Receipt'}
              </Button>
              <Button variant="contained" color="secondary" onClick={handleDownload}>
                Download PDF
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default TenantReceipt;
